import { useEffect, useState } from 'react';

import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _keyBy from 'lodash/keyBy';

import {
  filterStyleRuleConfigurationsByLocalSearchQuery,
  filterStyleRuleConfigurationsFolder,
  createStyleRuleConfigurationsFolderItems,
  filterStyleRuleConfigurationsByState,
  filterStyleRuleConfigurationsByUnit,
  getStyleRuleInheritanceFromConfigurations,
  fillCustomStyleRulesWithConfigurations,
} from './useStyleRuleSettings.utils';
import useStyleRuleConfigurations from '../useStyleRuleConfigurations';
import useStyleRules from '../useStyleRules';

export default function useStyleRuleSettings() {
  const { styleRules } = useStyleRules();

  const { styleRuleConfigurations } = useStyleRuleConfigurations();

  const [currentStyleRuleConfigurations, setCurrentStyleRuleConfigurations] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState([]);
  const [selectedUnitFilters, setSelectedUnitFilters] = useState([]);
  const [selectedStateFilters, setSelectedStateFilters] = useState([]);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [highlightedStyleRule, setHighlightedSelectedStyleRule] = useState(null);

  const styleRuleConfigurationsMap = _keyBy(styleRuleConfigurations, 'name');

  function onResetFilters() {
    setSelectedStateFilters([]);
    setSelectedUnitFilters([]);
  }

  function onSelectHighlightedStyleRule(styleRuleName) {
    setHighlightedSelectedStyleRule(styleRuleName);
    setSelectedFolder(styleRuleConfigurationsMap[styleRuleName].hierarchy);
    setLocalSearchQuery('');
    onResetFilters();
  }

  function onSelectFolder(folder) {
    setHighlightedSelectedStyleRule(null);
    setLocalSearchQuery('');
    setSelectedFolder((previousSelectedFolder) => {
      if (_isEqual(previousSelectedFolder, folder)) {
        return [];
      }

      return folder;
    });
    onResetFilters();
  }

  function onLocalSearchQueryChange(query) {
    setLocalSearchQuery(query);
  }

  function getStyleRuleConfigurationsFolderItems() {
    return createStyleRuleConfigurationsFolderItems(styleRuleConfigurations);
  }

  function onSelectStateFilter(nextState) {
    setSelectedStateFilters((previousSelectedStateFilters) => {
      if (previousSelectedStateFilters.includes(nextState)) {
        return previousSelectedStateFilters.filter((state) => state !== nextState);
      }

      return [...previousSelectedStateFilters, nextState];
    });
  }

  function onSelectUnitFilter(nextUnit) {
    setSelectedUnitFilters((previousSelectedUnitFilters) => {
      if (previousSelectedUnitFilters.includes(nextUnit)) {
        return previousSelectedUnitFilters.filter((unit) => unit !== nextUnit);
      }

      return [...previousSelectedUnitFilters, nextUnit];
    });
  }

  function getStyleRuleInheritance(styleRuleValue) {
    return getStyleRuleInheritanceFromConfigurations(
      styleRuleValue,
      styleRules,
      styleRuleConfigurationsMap,
    );
  }

  useEffect(() => {
    let newSelectedStyleRuleConfigurations = [...styleRuleConfigurations];

    newSelectedStyleRuleConfigurations = fillCustomStyleRulesWithConfigurations(
      newSelectedStyleRuleConfigurations,
      styleRules,
    );

    if (!_isEmpty(selectedFolder)) {
      newSelectedStyleRuleConfigurations = filterStyleRuleConfigurationsFolder(
        newSelectedStyleRuleConfigurations,
        selectedFolder,
      );
    }

    if (!_isEmpty(selectedUnitFilters)) {
      newSelectedStyleRuleConfigurations = filterStyleRuleConfigurationsByUnit(
        newSelectedStyleRuleConfigurations,
        selectedUnitFilters,
      );
    }

    if (!_isEmpty(selectedStateFilters)) {
      newSelectedStyleRuleConfigurations = filterStyleRuleConfigurationsByState(
        newSelectedStyleRuleConfigurations,
        selectedStateFilters,
        styleRules,
      );
    }

    if (!_isEmpty(localSearchQuery)) {
      newSelectedStyleRuleConfigurations = filterStyleRuleConfigurationsByLocalSearchQuery(
        newSelectedStyleRuleConfigurations,
        localSearchQuery,
      );
    }

    setCurrentStyleRuleConfigurations(newSelectedStyleRuleConfigurations);
  }, [
    styleRuleConfigurations,
    styleRules,
    selectedFolder,
    selectedUnitFilters,
    selectedStateFilters,
    localSearchQuery,
  ]);

  return {
    styleRules,
    styleRuleConfigurations,
    currentStyleRuleConfigurations,
    setCurrentStyleRuleConfigurations,
    selectedFolder,
    onSelectFolder,
    selectedUnitFilters,
    onSelectUnitFilter,
    selectedStateFilters,
    onSelectStateFilter,
    onResetFilters,
    localSearchQuery,
    setLocalSearchQuery,
    onLocalSearchQueryChange,
    highlightedStyleRule,
    onSelectHighlightedStyleRule,
    getStyleRuleConfigurationsFolderItems,
    getStyleRuleInheritance,
  };
}
